import { useEffect } from 'react';
import type { AIRateLimit } from '@bridebook/web/lib/ai-common/rate-limit-types';

/**
 * Fires `onExpire` once `resetAt` passes — used by a chat provider to clear
 * the rate-limit state, re-enabling the prompt input and removing the
 * "Not sent" badge automatically when the window resets.
 *
 * No ticking. The provider's context value is memoised on `rateLimit` identity,
 * so a tick driven from here would invalidate the value (or worse, be silently
 * dropped if not part of the memo). Per-minute re-renders for the countdown
 * text belong to the banner.
 */
export const useRateLimitExpire = (rateLimit: AIRateLimit | null, onExpire: () => void): void => {
  useEffect(() => {
    if (!rateLimit) return;
    const msUntilReset = rateLimit.resetAt.getTime() - Date.now();
    if (msUntilReset <= 0) {
      onExpire();
      return;
    }
    const id = window.setTimeout(onExpire, msUntilReset);
    return () => window.clearTimeout(id);
  }, [rateLimit, onExpire]);
};
